import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import api from "@/utils/api";
import {
  ArrowLeft, Printer, Download, Wrench, Plus, X, AlertTriangle, CheckCircle2, Search, Edit3, TrendingUp
} from "lucide-react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";

const ToolHistory = () => {
  const { toolNo: paramTool } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [toolNo, setToolNo] = useState(paramTool || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tools, setTools] = useState([]);
  const [showPmModal, setShowPmModal] = useState(false);
  const [showEditHeader, setShowEditHeader] = useState(false);
  const [toast, setToast] = useState("");

  const canLogPm = user?.role === "Main Admin" || user?.role === "Supervisor";
  const canEditHeader = user?.role === "Main Admin";

  useEffect(() => {
    api.get("/tools").then((r) => setTools(r.data || []));
  }, []);

  const fetchData = (tn) => {
    if (!tn) return;
    setLoading(true);
    setError("");
    api.get(`/tool-history/${tn}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (toolNo) fetchData(toolNo); }, [toolNo]);

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(""), 3000); };

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const header = ["Date", "Machine No.", "Operator Name", "OK Parts", "NG Parts",
      "Qty Produced", "Total Cum Qty Strokes", "PM Tracking Qty",
      "Description of Maintenance", "PM Date", "Cause", "Action Taken"];
    const rows = data.rows.map((r) => [
      r.date, r.machine, r.operator, r.ok_parts, r.ng_parts, r.qty_produced,
      r.cum_strokes, r.pm_tracking_qty, r.pm_marker, r.pm_event_date,
      `"${(r.cause || "").replace(/"/g, '""')}"`,
      `"${(r.action_taken || "").replace(/"/g, '""')}"`,
    ]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tool-history-${toolNo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalStrokes = data?.totals?.total_strokes || 0;
  const toolLife = data?.header?.tool_life || 0;
  const lifePct = toolLife > 0 ? Math.min(100, Math.round((totalStrokes / toolLife) * 100)) : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-4 print:hidden">
          <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
            <ArrowLeft size={14} /> Back
          </button>
          <div className="flex items-center gap-2">
            {canLogPm && data && (
              <button
                data-testid="th-log-pm"
                onClick={() => setShowPmModal(true)}
                className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 px-3 py-2 rounded-lg"
              >
                <Plus size={14} /> Log PM / Breakdown
              </button>
            )}
            <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 px-3 py-2 rounded-lg">
              <Printer size={14} /> Print
            </button>
            <button data-testid="th-export" onClick={exportCsv} disabled={!data?.rows?.length} className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-[#1A3560] hover:bg-[#152a4d] disabled:bg-slate-300 px-3 py-2 rounded-lg">
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        {toast && (
          <div className="fixed top-20 right-4 z-50 bg-slate-900 text-white px-4 py-2 rounded-lg shadow-2xl text-sm font-medium">{toast}</div>
        )}

        {/* Tool switcher */}
        {!paramTool && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 mb-4 flex items-center gap-2 print:hidden">
            <Search size={16} className="text-slate-400" />
            <select
              data-testid="th-tool-select"
              value={toolNo}
              onChange={(e) => setToolNo(e.target.value)}
              className="flex-1 text-sm border-none outline-none bg-transparent"
            >
              <option value="">— Select tool —</option>
              {tools.map((t) => (
                <option key={t.tool_no} value={t.tool_no}>
                  T{t.tool_no} · {t.part_name} ({t.customer || "?"})
                </option>
              ))}
            </select>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800 mb-4">{error}</div>
        )}

        {loading ? (
          <div className="text-center text-slate-400 py-10">Loading...</div>
        ) : !toolNo ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-10 text-center">
            <Wrench size={40} className="mx-auto text-slate-300 mb-2" />
            <p className="font-extrabold text-slate-700">Choose a tool to view its history card</p>
          </div>
        ) : data && (
          <>
            {/* Header card (matches Excel layout) */}
            <div className="bg-white rounded-2xl shadow-sm border-2 border-slate-300 mb-4 overflow-hidden">
              <div className="bg-[#1A3560] text-white px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Die History Card</p>
                  <h1 className="text-2xl font-extrabold">Tool T{data.header.tool_no}</h1>
                </div>
                <div className="flex items-center gap-3">
                  {canEditHeader && (
                    <button
                      data-testid="th-edit-header"
                      onClick={() => setShowEditHeader(true)}
                      className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/25 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
                    >
                      <Edit3 size={12} /> Edit Header
                    </button>
                  )}
                  <p className="text-[10px] opacity-70 font-mono">Doc No: AMPL-PRD-F26 · Rev 01</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 p-5 text-sm">
                <HeaderRow label="Tool No." value={`T${data.header.tool_no}`} />
                <HeaderRow label="Tool Life" value={data.header.tool_life?.toLocaleString() || "—"} />
                <HeaderRow label="Part Name" value={data.header.part_name} />
                <HeaderRow label="Customer" value={data.header.customer} />
                <HeaderRow label="Part No." value={data.header.part_no} />
                <HeaderRow label="Last PM" value={data.header.last_pm_date || "—"} />
                <HeaderRow
                  label="Next PM At"
                  value={data.header.next_pm_at_cum_strokes ? `${data.header.next_pm_at_cum_strokes.toLocaleString()} strokes` : "—"}
                />
                <HeaderRow label="Operation" value={data.header.operation} />
                <HeaderRow label="Tool Type" value={data.header.tool_type} />
                <HeaderRow label="Cutting Material" value={data.header.cutting_material || "—"} />
                <HeaderRow label="Cavity" value={data.header.cavities} />
                <HeaderRow
                  label="Tool PM Stroke Range"
                  value={
                    data.header.pm_stroke_range_min || data.header.pm_stroke_range_max
                      ? `${data.header.pm_stroke_range_min || 0} – ${data.header.pm_stroke_range_max || 0}`
                      : "—"
                  }
                />
                <HeaderRow label="RM Grade / Spec" value={data.header.rm_grade ? `${data.header.rm_grade} · ${data.header.thickness_mm}×${data.header.width_mm}mm` : "—"} />
              </div>

              {toolLife > 0 && (
                <div className="px-5 pb-4">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-500 font-bold">Life used</span>
                    <span className="font-extrabold text-slate-800">{totalStrokes.toLocaleString()} / {toolLife.toLocaleString()} ({lifePct}%)</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5">
                    <div
                      className={`h-2.5 rounded-full ${lifePct > 90 ? "bg-red-500" : lifePct > 70 ? "bg-amber-500" : "bg-emerald-500"}`}
                      style={{ width: `${lifePct}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Tool Life Trend chart (last 30 days) */}
            {data.rows.length > 0 && (() => {
              // Aggregate rows by date; add zero-fill days for last 30 days.
              const byDate = {};
              data.rows.forEach((r) => {
                const d = r.date;
                if (!d) return;
                if (!byDate[d]) byDate[d] = { date: d, ok: 0, ng: 0, strokes: 0 };
                byDate[d].ok += r.ok_parts || 0;
                byDate[d].ng += r.ng_parts || 0;
                byDate[d].strokes += r.strokes || 0;
              });
              const today = new Date();
              const days = [];
              for (let i = 29; i >= 0; i--) {
                const dt = new Date(today);
                dt.setDate(dt.getDate() - i);
                const off = dt.getTimezoneOffset() * 60000;
                const iso = new Date(dt.getTime() - off).toISOString().slice(0, 10);
                days.push(iso);
              }
              let cum = 0;
              // Compute starting cum for the earliest visible day
              // by summing strokes for all rows *before* the first day.
              const firstDay = days[0];
              const before = data.rows.filter((r) => r.date && r.date < firstDay);
              cum = before.reduce((s, r) => s + (r.strokes || 0), 0);
              const chartData = days.map((d) => {
                const row = byDate[d] || { date: d, ok: 0, ng: 0, strokes: 0 };
                cum += row.strokes;
                return { day: d.slice(5), ok: row.ok, ng: row.ng, cum };
              });
              return (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-4">
                  <p className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mb-2 inline-flex items-center gap-1">
                    <TrendingUp size={12} /> Tool life trend — last 30 days
                  </p>
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="parts" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="cum" orientation="right" tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar yAxisId="parts" dataKey="ok" name="OK" stackId="p" fill="#15803D" />
                      <Bar yAxisId="parts" dataKey="ng" name="NG" stackId="p" fill="#DC2626" />
                      <Line yAxisId="cum" type="monotone" dataKey="cum" name="Cum Strokes" stroke="#1A3560" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}

            {/* Rows table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">Production &amp; PM history</p>
                <p className="text-xs text-slate-400">{data.totals.rows} row(s) · {data.totals.pm_events} PM event(s)</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Machine</th>
                      <th className="px-3 py-2 text-left">Operator</th>
                      <th className="px-3 py-2 text-right">OK</th>
                      <th className="px-3 py-2 text-right">NG</th>
                      <th className="px-3 py-2 text-right">Qty Produced</th>
                      <th className="px-3 py-2 text-right">Cum Strokes</th>
                      <th className="px-3 py-2 text-right">Since Last PM</th>
                      <th className="px-3 py-2 text-left">PM</th>
                      <th className="px-3 py-2 text-left">Cause</th>
                      <th className="px-3 py-2 text-left">Action Taken</th>
                    </tr>
                  </thead>
                  <tbody data-testid="th-tbody">
                    {data.rows.length === 0 ? (
                      <tr><td colSpan={11} className="text-center text-slate-400 py-6">No production history yet</td></tr>
                    ) : data.rows.map((r) => (
                      <tr key={r.job_id} className={`border-b border-slate-100 ${r.pm_marker ? "bg-amber-50" : "hover:bg-slate-50"}`}>
                        <td className="px-3 py-1.5 font-mono">{r.date}</td>
                        <td className="px-3 py-1.5">{r.machine}</td>
                        <td className="px-3 py-1.5">{r.operator}</td>
                        <td className="px-3 py-1.5 text-right font-bold text-emerald-700">{r.ok_parts}</td>
                        <td className="px-3 py-1.5 text-right font-bold text-red-600">{r.ng_parts}</td>
                        <td className="px-3 py-1.5 text-right">{r.qty_produced}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{r.cum_strokes.toLocaleString()}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{r.pm_tracking_qty.toLocaleString()}</td>
                        <td className="px-3 py-1.5">
                          {r.pm_marker && (
                            <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full bg-amber-500 text-white">
                              {r.pm_marker}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-slate-600 max-w-xs">{r.cause || "—"}</td>
                        <td className="px-3 py-1.5 text-slate-600 max-w-xs">{r.action_taken || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {data.orphan_pm_events.length > 0 && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                <AlertTriangle size={12} className="inline" />&nbsp;
                {data.orphan_pm_events.length} PM/Breakdown event(s) logged on dates without any production job — shown below:
                <ul className="mt-2 space-y-1">
                  {data.orphan_pm_events.map((e) => (
                    <li key={e.event_id} className="font-mono">{e.event_date} — {e.event_type} — {e.cause} — {e.action_taken}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Mount / Un-mount timeline */}
            {data.mount_events && data.mount_events.length > 0 && (
              <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="tool-mount-timeline">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                  <Wrench size={14} className="text-slate-500" />
                  <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">
                    Mount / Un-mount Timeline ({data.mount_events.length})
                  </p>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase font-extrabold text-slate-400">
                    <tr>
                      <th className="px-3 py-2 text-left">Date &amp; Time</th>
                      <th className="px-3 py-2 text-left">Event</th>
                      <th className="px-3 py-2 text-left">Machine</th>
                      <th className="px-3 py-2 text-left">Reason</th>
                      <th className="px-3 py-2 text-left">Remark</th>
                      <th className="px-3 py-2 text-left">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.mount_events.map((e) => (
                      <tr key={e.event_id} className={e.event_type === "unmounted" ? "bg-red-50/40" : "bg-emerald-50/30"}>
                        <td className="px-3 py-1.5 font-mono text-slate-600">
                          {new Date(e.event_time).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </td>
                        <td className="px-3 py-1.5">
                          <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${e.event_type === "mounted" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                            {e.event_type === "mounted" ? "MOUNTED" : "UN-MOUNTED"}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 font-bold text-slate-900">{e.machine_code}</td>
                        <td className="px-3 py-1.5 text-slate-600">{e.reason_label || "—"}</td>
                        <td className="px-3 py-1.5 text-slate-500 italic max-w-xs truncate" title={e.remark || ""}>{e.remark || "—"}</td>
                        <td className="px-3 py-1.5 text-slate-500">{e.actor_name || e.actor || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {showPmModal && (
          <PmModal
            toolNo={toolNo}
            onClose={() => setShowPmModal(false)}
            onSaved={() => { setShowPmModal(false); showToast("PM event logged"); fetchData(toolNo); }}
          />
        )}

        {showEditHeader && data && (
          <EditHeaderModal
            toolNo={toolNo}
            header={data.header}
            onClose={() => setShowEditHeader(false)}
            onSaved={() => { setShowEditHeader(false); showToast("Tool metadata updated"); fetchData(toolNo); }}
          />
        )}
      </div>
    </div>
  );
};

const HeaderRow = ({ label, value }) => (
  <div className="flex items-baseline border-b border-slate-100 py-1">
    <span className="text-xs font-bold text-slate-500 w-40 shrink-0">{label}</span>
    <span className="text-sm text-slate-900 font-semibold">{value || "—"}</span>
  </div>
);

const todayIso = () => {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
};

const PmModal = ({ toolNo, onClose, onSaved }) => {
  const [form, setForm] = useState({
    event_date: todayIso(),
    event_type: "PM",
    cause: "",
    action_taken: "",
    description: "",
    at_cum_strokes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!form.action_taken.trim()) {
      setErr("Action taken is required");
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      const payload = {
        tool_no: toolNo,
        event_date: form.event_date,
        event_type: form.event_type,
        cause: form.cause,
        action_taken: form.action_taken,
        description: form.description,
      };
      if (form.at_cum_strokes) payload.at_cum_strokes = parseInt(form.at_cum_strokes);
      await api.post("/tool-history/pm-events", payload);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="bg-amber-600 text-white px-5 py-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Log PM / Breakdown</p>
            <p className="font-extrabold">Tool T{toolNo}</p>
          </div>
          <button onClick={onClose} className="bg-white/20 hover:bg-white/30 rounded-full p-1.5"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {err && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-bold text-slate-700">Event Type
              <select data-testid="pm-type" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2">
                <option value="PM">PM</option>
                <option value="Breakdown">Breakdown</option>
              </select>
            </label>
            <label className="text-xs font-bold text-slate-700">Event Date
              <input data-testid="pm-date" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2" />
            </label>
          </div>
          <label className="block text-xs font-bold text-slate-700">Cause
            <input data-testid="pm-cause" value={form.cause} onChange={(e) => setForm({ ...form, cause: e.target.value })} placeholder="e.g. Flatness 0.09 / Line mark observed" className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2" />
          </label>
          <label className="block text-xs font-bold text-slate-700">Action Taken *
            <input data-testid="pm-action" value={form.action_taken} onChange={(e) => setForm({ ...form, action_taken: e.target.value })} placeholder="e.g. Regrinding done / Polished on lath machine" className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2" />
          </label>
          <label className="block text-xs font-bold text-slate-700">At Cum Strokes (optional)
            <input data-testid="pm-cumstrokes" type="number" min={0} value={form.at_cum_strokes} onChange={(e) => setForm({ ...form, at_cum_strokes: e.target.value })} placeholder="Auto-calculated if blank" className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2" />
          </label>
          <label className="block text-xs font-bold text-slate-700">Description
            <textarea data-testid="pm-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Detailed remarks (optional)" className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2" />
          </label>
        </div>
        <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm font-bold text-slate-600 px-3 py-2 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button data-testid="pm-submit" onClick={submit} disabled={submitting} className="text-sm font-extrabold text-white bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 px-4 py-2 rounded-lg inline-flex items-center gap-1.5">
            <CheckCircle2 size={14} /> {submitting ? "Saving…" : "Save PM event"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToolHistory;

const EditHeaderModal = ({ toolNo, header, onClose, onSaved }) => {
  const [form, setForm] = useState({
    tool_life: header.tool_life || "",
    tool_type: header.tool_type || "",
    cutting_material: header.cutting_material || "",
    pm_stroke_range_min: header.pm_stroke_range_min || "",
    pm_stroke_range_max: header.pm_stroke_range_max || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    try {
      const payload = {};
      if (form.tool_life !== "") payload.tool_life = parseInt(form.tool_life);
      if (form.tool_type) payload.tool_type = form.tool_type;
      if (form.cutting_material) payload.cutting_material = form.cutting_material;
      if (form.pm_stroke_range_min !== "") payload.pm_stroke_range_min = parseInt(form.pm_stroke_range_min);
      if (form.pm_stroke_range_max !== "") payload.pm_stroke_range_max = parseInt(form.pm_stroke_range_max);
      await api.put(`/admin/tools/${toolNo}`, payload);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#1A3560] text-white px-5 py-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Edit Tool Header</p>
            <p className="font-extrabold">Tool T{toolNo}</p>
          </div>
          <button onClick={onClose} className="bg-white/20 hover:bg-white/30 rounded-full p-1.5">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {err && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">{err}</div>}
          <label className="block text-xs font-bold text-slate-700">Tool Life (total strokes)
            <input
              data-testid="edit-tool-life"
              type="number" min={0}
              value={form.tool_life}
              onChange={(e) => setForm({ ...form, tool_life: e.target.value })}
              placeholder="e.g. 2000000"
              className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-bold text-slate-700">Tool Type
              <select
                data-testid="edit-tool-type"
                value={form.tool_type}
                onChange={(e) => setForm({ ...form, tool_type: e.target.value })}
                className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
              >
                <option value="">— select —</option>
                <option value="Press Tool">Press Tool</option>
                <option value="Draw Die">Draw Die</option>
                <option value="Cutting Die">Cutting Die</option>
                <option value="Progressive Die">Progressive Die</option>
                <option value="Blanking Die">Blanking Die</option>
                <option value="Bending Die">Bending Die</option>
                <option value="Compound Die">Compound Die</option>
                <option value="Other">Other</option>
              </select>
            </label>
            <label className="text-xs font-bold text-slate-700">Cutting Material
              <input
                data-testid="edit-cutting-material"
                value={form.cutting_material}
                onChange={(e) => setForm({ ...form, cutting_material: e.target.value })}
                placeholder="e.g. D2 / HSS / Carbide"
                className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-bold text-slate-700">PM Stroke Range Min
              <input
                data-testid="edit-pm-min"
                type="number" min={0}
                value={form.pm_stroke_range_min}
                onChange={(e) => setForm({ ...form, pm_stroke_range_min: e.target.value })}
                placeholder="e.g. 76500"
                className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
              />
            </label>
            <label className="text-xs font-bold text-slate-700">PM Stroke Range Max
              <input
                data-testid="edit-pm-max"
                type="number" min={0}
                value={form.pm_stroke_range_max}
                onChange={(e) => setForm({ ...form, pm_stroke_range_max: e.target.value })}
                placeholder="e.g. 90000"
                className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
              />
            </label>
          </div>
          <p className="text-[11px] text-slate-500">
            Only Main Admin can edit tool header metadata. PM reminders auto-notify Supervisor when strokes-since-last-PM exceeds the max.
          </p>
        </div>
        <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm font-bold text-slate-600 px-3 py-2 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button
            data-testid="edit-header-submit"
            onClick={submit}
            disabled={submitting}
            className="text-sm font-extrabold text-white bg-[#1A3560] hover:bg-[#152a4d] disabled:bg-slate-300 px-4 py-2 rounded-lg inline-flex items-center gap-1.5"
          >
            <CheckCircle2 size={14} /> {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
